import { useState, useMemo, useEffect, Fragment } from "react";
import { api, apiErr, API } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { SidebarShell, StatCard, PageHeader, Empty, NotificationBell } from "@/components/Shell";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useFetch, fmtDate, StatusBadge, Field } from "@/lib/ui";
import HomeworkBoard from "@/components/HomeworkBoard";
import Exams from "@/pages/Exams";
import ExamBank from "@/components/ExamBank";
import TermGrades from "@/components/TermGrades";
import HonourRoll from "@/components/HonourRoll";
import Discipline from "@/components/Discipline";
import AnnualCalendar from "@/components/AnnualCalendar";
import LessonPlanner from "@/components/LessonPlanner";
import ModulesPanel from "@/components/ModulesPanel";
import LmsLibrary from "@/components/LmsLibrary";
import { GradeSubmissionBadge } from "@/components/GradeApproval";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LayoutDashboard, School, CalendarCheck, BookOpen, ClipboardList, CalendarDays, Plus, Save, GraduationCap, Users, Download, FolderOpen, Trophy, ShieldAlert, Utensils, ChevronLeft, ChevronRight, FileText, Bell } from "lucide-react";
import { TeacherLessonBook } from "@/components/CahierPedagogique";

const TERMS = ["Term 1", "Term 2", "Term 3", "Term 4"];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

function StaffInbox({ L }) {
  const { data: notifs, reload } = useFetch(() => api.get("/my-notifications"));
  const unread = (notifs || []).filter((n) => !n.read);
  const dismiss = async () => { try { await api.post("/my-notifications/read", {}); reload(); } catch (e) { /* noop */ } };
  if (unread.length === 0) return null;
  return (
    <div className="mb-6 space-y-3" data-testid="staff-inbox">
      {unread.map((n) => (
        <div key={n.id} className="bg-primary/5 border border-primary/30 rounded-lg p-4" data-testid={`staff-notif-${n.id}`}>
          <p className="font-heading font-bold text-primary flex items-center gap-2"><Bell className="h-4 w-4" />{n.title || L("Notification", "Notification")}</p>
          <p className="text-sm text-foreground/80 mt-1 whitespace-pre-line">{n.message}</p>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={dismiss} data-testid="staff-inbox-dismiss">{L("Dismiss all", "Tout fermer")}</Button>
    </div>
  );
}

export default function TeacherPortal() {
  const { L } = useLang();
  const nav = [
    { key: "dashboard", label: L("Dashboard", "Tableau de bord"), icon: LayoutDashboard },
    { key: "classes", label: L("My Classes", "Mes classes"), icon: School },
    { key: "attendance", label: L("Attendance", "Présence"), icon: CalendarCheck },
    { key: "grades", label: L("Grades", "Notes"), icon: BookOpen },
    { key: "term-grades", label: L("Term Grade", "Note du terme"), icon: GraduationCap },
    { key: "honour-roll", label: L("Honour Roll", "Tableau d'Honneur"), icon: Trophy },
    { key: "discipline", label: L("Discipline", "Discipline"), icon: ShieldAlert },
    { key: "homework", label: L("Homework", "Devoirs"), icon: ClipboardList },
    { key: "lesson-planner", label: L("Lesson Planner", "Planificateur"), icon: BookOpen },
    { key: "cahier", label: L("Teaching Logbook", "Cahier de bord"), icon: FileText },
    { key: "exams", label: L("Exams", "Examens"), icon: BookOpen },
    { key: "exam-bank", label: L("Exam Bank", "Dépôt d'examens"), icon: FolderOpen },
    { key: "lms", label: L("Curriculum / Books", "Curriculum / Livres"), icon: BookOpen },
    { key: "calendar", label: L("Calendar", "Calendrier"), icon: CalendarDays },
    { key: "schedule", label: L("Schedule", "Horaire"), icon: CalendarDays },
  ];
  const [active, setActive] = useState("dashboard");
  const title = nav.find((n) => n.key === active)?.label;
  return (
    <SidebarShell nav={nav} active={active} onNav={setActive} title={title} headerExtra={<NotificationBell />}>
      <ErrorBoundary key={active} compact>
      <StaffInbox L={L} />
      {active === "dashboard" && <Dashboard L={L} onNav={setActive} />}
      {active === "classes" && <Classes L={L} />}
      {active === "attendance" && <Attendance L={L} />}
      {active === "grades" && <Grades L={L} />}
      {active === "term-grades" && <TermGrades />}
      {active === "honour-roll" && <HonourRoll />}
      {active === "discipline" && <Discipline />}
      {active === "homework" && <HomeworkBoard isAdmin={false} />}
      {active === "lesson-planner" && <LessonPlanner />}
      {active === "cahier" && <TeacherLessonBook />}
      {active === "exams" && <Exams />}
      {active === "exam-bank" && <ExamBank />}
      {active === "calendar" && <AnnualCalendar canEdit={false} />}
      {active === "schedule" && <Schedule L={L} />}
      {active === "lms" && <TeacherLms L={L} />}
      </ErrorBoundary>
    </SidebarShell>
  );
}

function TeacherLms({ L }) {
  const [tab, setTab] = useState("books");
  return (
    <div className="animate-fade-up">
      <PageHeader title={L("Curriculum & Books", "Curriculum & Livres")}
        description={L("Your class books and weekly lessons — free to use with your students.", "Vos livres de classe et leçons hebdomadaires — libres d'utilisation avec vos élèves.")} />
      <div className="flex gap-2 mb-6">
        <Button variant={tab === "books" ? "default" : "outline"} onClick={() => setTab("books")} data-testid="tlms-tab-books"><BookOpen className="h-4 w-4 mr-1" />{L("Books", "Livres")}</Button>
        <Button variant={tab === "curriculum" ? "default" : "outline"} onClick={() => setTab("curriculum")} data-testid="tlms-tab-curriculum"><GraduationCap className="h-4 w-4 mr-1" />{L("Curriculum & Progress", "Curriculum & Progression")}</Button>
      </div>
      {tab === "books" ? <LmsLibrary /> : <ModulesPanel canManage={false} />}
    </div>
  );
}

function TeacherBooks({ L }) {
  const { data: docs, loading } = useFetch(() => api.get("/lms/documents"));
  const [viewing, setViewing] = useState(null);
  if (loading) return <Empty title={L("Loading…", "Chargement…")} />;
  const byGrade = {}; (docs || []).forEach((d) => { (byGrade[d.grade_level] = byGrade[d.grade_level] || []).push(d); });
  if (Object.keys(byGrade).length === 0) return <Empty title={L("No books uploaded yet", "Aucun livre téléversé")} />;
  return (
    <div className="space-y-6">
      {Object.keys(byGrade).map((g) => (
        <div key={g} data-testid={`tlms-grade-${g}`}>
          <p className="font-heading font-bold text-lg mb-2 flex items-center gap-2"><BookOpen className="h-5 w-5 text-primary" />{g}</p>
          <div className="space-y-2">
            {byGrade[g].map((d) => (
              <div key={d.id} className="bg-card border border-border rounded-lg" data-testid={`tlms-doc-${d.id}`}>
                <div className="p-4 flex items-center justify-between gap-2">
                  <span className="font-medium">{d.title}</span>
                  <Button size="sm" variant="outline" onClick={() => setViewing(viewing === d.id ? null : d.id)} data-testid={`tlms-view-${d.id}`}>{viewing === d.id ? L("Hide", "Masquer") : L("Open", "Ouvrir")}</Button>
                </div>
                {viewing === d.id && (
                  <div className="border-t border-border p-3">
                    <div className="relative w-full" style={{ height: "70vh" }}>
                      <iframe title={d.title} src={`${API}/lms/documents/${d.id}/view#toolbar=0&navpanes=0`} className="w-full h-full border border-border rounded" data-testid={`tlms-frame-${d.id}`} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Dashboard({ L, onNav }) {
  const { data: s } = useFetch(() => api.get("/stats"));
  const st = s || {};
  return (
    <div className="space-y-6 animate-fade-up">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard label={L("My Classes", "Mes classes")} value={st.classes ?? "—"} icon={School} />
        <StatCard label={L("Students", "Élèves")} value={st.students ?? "—"} icon={Users} />
        <StatCard label={L("Homework Posted", "Devoirs publiés")} value={st.homework ?? "—"} icon={ClipboardList} tone="accent" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {[["attendance", L("Take Attendance", "Faire l'appel"), CalendarCheck], ["grades", L("Enter Grades", "Saisir les notes"), BookOpen], ["homework", L("Set Homework", "Donner un devoir"), ClipboardList]].map(([k, l, I]) => (
          <button key={k} onClick={() => onNav(k)} data-testid={`quick-${k}`} className="text-left bg-card border border-border rounded-lg p-5 hover:-translate-y-1 hover:shadow-md transition-transform">
            <I className="h-6 w-6 text-primary" /><p className="font-heading font-semibold mt-3">{l}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function Classes({ L }) {
  const { data: classes, loading } = useFetch(() => api.get("/classes?mine=1"));
  if (loading) return <Empty title={L("Loading…", "Chargement…")} />;
  return (
    <div className="animate-fade-up">
      <PageHeader title={L("My Classes", "Mes classes")} description={L("Classes assigned to you.", "Les classes qui vous sont assignées.")} />
      {(classes || []).length === 0 ? <Empty title={L("No classes assigned", "Aucune classe assignée")} /> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {classes.map((c) => (
            <div key={c.id} className="bg-card border border-border rounded-lg p-5" data-testid={`class-card-${c.id}`}>
              <p className="font-heading font-bold text-lg">{c.name}</p>
              <p className="text-sm text-muted-foreground">{c.grade_level} · Room {c.room || "—"}</p>
              <Badge variant="secondary" className="mt-3">{c.student_count} {L("students", "élèves")}</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClassStudentPicker({ L, classId, setClassId, testid }) {
  const { data: classes } = useFetch(() => api.get("/classes?mine=1"));
  return (
    <Select value={classId} onValueChange={setClassId}>
      <SelectTrigger className="w-72" data-testid={testid}><SelectValue placeholder={L("Select class", "Choisir une classe")} /></SelectTrigger>
      <SelectContent>{(classes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.grade_level}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function Attendance({ L }) {
  const [classId, setClassId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const { data: students } = useFetch(() => (classId ? api.get("/students", { params: { class_id: classId, status: "accepted" } }) : Promise.resolve([])), [classId]);
  const [marks, setMarks] = useState({});
  const set = (id, st) => setMarks((m) => ({ ...m, [id]: st }));
  const save = async () => {
    const records = (students || []).map((s) => ({ student_id: s.id, status: marks[s.id] || "present" }));
    if (records.length === 0) return;
    try { await api.post("/attendance", { class_id: classId, date, records }); toast.success(L("Attendance submitted to the administration for review. Parents are not notified directly.", "Appel soumis à l'administration pour validation. Les parents ne sont pas notifiés directement.")); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className="animate-fade-up">
      <PageHeader title={L("Attendance", "Présence")} description={L("Your markings go to the admin dashboard for review — the office notifies parents, not you.", "Vos saisies vont au tableau de bord de l'administration pour validation — c'est le bureau qui notifie les parents, pas vous.")} />
      <div className="flex flex-wrap gap-3 mb-6">
        <ClassStudentPicker L={L} classId={classId} setClassId={setClassId} testid="attendance-class-select" />
        <Input type="date" className="w-44" value={date} onChange={(e) => setDate(e.target.value)} data-testid="attendance-date-input" />
      </div>
      {!classId ? <Empty title={L("Select a class", "Choisir une classe")} /> : (students || []).length === 0 ? <Empty title={L("No students in this class", "Aucun élève dans cette classe")} /> : (
        <div className="space-y-3">
          <div className="space-y-2">
            {students.map((s) => (
              <div key={s.id} className="flex items-center justify-between bg-card border border-border rounded-lg p-3" data-testid={`att-student-${s.id}`}>
                <span className="font-medium">{s.first_name} {s.last_name}</span>
                <div className="flex gap-1">
                  {["present", "late", "absent"].map((st) => (
                    <button key={st} onClick={() => set(s.id, st)} data-testid={`att-${s.id}-${st}`}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize border transition-colors ${((marks[s.id] || "present") === st) ? (st === "present" ? "bg-emerald-600 text-white border-emerald-600" : st === "late" ? "bg-amber-500 text-white border-amber-500" : "bg-destructive text-white border-destructive") : "border-border text-muted-foreground hover:bg-secondary"}`}>
                      {L(st, st === "present" ? "présent" : st === "late" ? "retard" : "absent")}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Button onClick={save} data-testid="save-attendance-btn"><Save className="h-4 w-4 mr-1" />{L("Save Attendance", "Enregistrer")}</Button>
        </div>
      )}
    </div>
  );
}

const GRADE_CATS = [
  { v: "exam", en: "Exam", fr: "Examen" },
  { v: "quiz", en: "Quiz", fr: "Quiz" },
  { v: "project", en: "Project", fr: "Projet" },
  { v: "participation", en: "Participation", fr: "Participation" },
  { v: "extra_credit", en: "Extra credit", fr: "Points bonus" },
];
const catLabel = (v, L) => { const c = GRADE_CATS.find((x) => x.v === v); return c ? L(c.en, c.fr) : v; };

function Grades({ L }) {
  const [classId, setClassId] = useState("");
  const [term, setTerm] = useState("Term 1");
  const [studentId, setStudentId] = useState("");
  const { data: teaching } = useFetch(() => api.get("/me/teaching"));
  const { data: students } = useFetch(() => (classId ? api.get("/students", { params: { class_id: classId, status: "accepted" } }) : Promise.resolve([])), [classId]);
  const { data: rc, reload } = useFetch(() => (studentId ? api.get(`/students/${studentId}/report-card`, { params: { term } }) : Promise.resolve(null)), [studentId, term]);
  const [g, setG] = useState({ subject: "", category: "exam", score: "", max_score: "100", remarks: "" });
  // When a class is chosen, auto-select its first student so the grade table shows immediately.
  useEffect(() => {
    if (classId && !studentId && (students || []).length > 0) setStudentId(students[0].id);
  }, [classId, students, studentId]);
  const lockSubjects = teaching?.scoped && (teaching.subjects || []).length > 0 && !(teaching.homeroom_class_ids || []).includes(classId);
  const allowed = teaching?.subjects || [];
  const { data: subStatus, reload: reloadSub } = useFetch(
    () => (classId && g.subject && term ? api.get("/grades/submission-status", { params: { class_id: classId, subject: g.subject, term } }) : Promise.resolve({ status: "none" })),
    [classId, g.subject, term]);
  const locked = subStatus?.status === "approved";
  const add = async () => {
    if (!g.subject || g.score === "") { toast.error(L("Enter subject and score", "Saisir matière et note")); return; }
    try { await api.post("/grades", { student_id: studentId, class_id: classId, term, subject: g.subject, category: g.category, score: parseFloat(g.score), max_score: parseFloat(g.max_score), remarks: g.remarks }); toast.success(L("Grade saved", "Note enregistrée")); setG({ ...g, score: "", remarks: "" }); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const submit = async () => {
    if (!classId || !g.subject) { toast.error(L("Select class and subject", "Choisir classe et matière")); return; }
    try { await api.post("/grades/submit", { class_id: classId, subject: g.subject, term }); toast.success(L("Submitted for approval", "Soumis pour approbation")); reloadSub(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className="animate-fade-up">
      <PageHeader title={L("Grades", "Notes")} description={L("Enter grades per class, then submit each subject for admin approval. Approved grades lock.", "Saisir les notes par classe, puis soumettre chaque matière pour approbation. Les notes approuvées se verrouillent.")} />
      <div className="flex flex-wrap gap-3 mb-6">
        <ClassStudentPicker L={L} classId={classId} setClassId={(v) => { setClassId(v); setStudentId(""); }} testid="grades-class-select" />
        {classId && (
          <Select value={studentId} onValueChange={setStudentId}>
            <SelectTrigger className="w-64" data-testid="grades-student-select"><SelectValue placeholder={L("Select student", "Choisir un élève")} /></SelectTrigger>
            <SelectContent>{(students || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {classId && studentId && (students || []).length > 1 && (() => {
          const idx = (students || []).findIndex((s) => s.id === studentId);
          return (
            <div className="flex items-center gap-1" data-testid="grades-student-nav">
              <Button variant="outline" size="icon" disabled={idx <= 0} onClick={() => idx > 0 && setStudentId(students[idx - 1].id)} data-testid="grades-prev-student" title={L("Previous student", "Élève précédent")}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-xs text-muted-foreground px-1 tabular-nums" data-testid="grades-student-pos">{idx + 1}/{students.length}</span>
              <Button variant="outline" size="icon" disabled={idx >= students.length - 1} onClick={() => idx < students.length - 1 && setStudentId(students[idx + 1].id)} data-testid="grades-next-student" title={L("Next student", "Élève suivant")}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          );
        })()}
        <Select value={term} onValueChange={setTerm}><SelectTrigger className="w-40" data-testid="grades-term-select"><SelectValue /></SelectTrigger><SelectContent>{TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
      </div>
      {!studentId ? <Empty title={L("Please choose a class and a student to enter grades", "Veuillez choisir une classe et un élève pour saisir les notes")} /> : (
        <div className="space-y-4 max-w-2xl">
          <div className="bg-card border border-border rounded-lg p-5">
            {rc && rc.grades.length > 0 ? (
              <Table>
                <TableHeader><TableRow><TableHead>{L("Subject", "Matière")}</TableHead><TableHead>{L("Category", "Catégorie")}</TableHead><TableHead>{L("Score", "Note")}</TableHead><TableHead>{L("Remarks", "Remarques")}</TableHead></TableRow></TableHeader>
                <TableBody>{rc.grades.map((gr) => <TableRow key={gr.id}><TableCell>{gr.subject}</TableCell><TableCell className="text-xs text-muted-foreground">{catLabel(gr.category || "exam", L)}</TableCell><TableCell>{gr.score}/{gr.max_score}</TableCell><TableCell className="text-muted-foreground">{gr.remarks || "—"}</TableCell></TableRow>)}</TableBody>
              </Table>
            ) : <p className="text-sm text-muted-foreground">{L("No grades yet.", "Aucune note pour l'instant.")}</p>}
          </div>
          <div className="bg-card border border-border rounded-lg p-5">
            <p className="font-heading font-semibold mb-3">{L("Add grade", "Ajouter une note")}</p>
            {lockSubjects && <p className="text-xs text-amber-700 mb-2" data-testid="tgrade-lock-note">{L(`You can only grade your assigned subjects: ${allowed.join(", ")}.`, `Vous ne pouvez noter que vos matières assignées : ${allowed.join(", ")}.`)}</p>}
            {locked && <p className="text-xs text-rose-700 mb-2 font-medium" data-testid="tgrade-locked-note">{L(`"${g.subject}" (${term}) is approved and locked. Ask the office to unlock.`, `« ${g.subject} » (${term}) est approuvé et verrouillé. Demandez le déblocage à l'administration.`)}</p>}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
              <Field label={L("Subject", "Matière")}>
                {lockSubjects ? (
                  <Select value={g.subject} onValueChange={(v) => setG({ ...g, subject: v })}>
                    <SelectTrigger data-testid="tgrade-subject"><SelectValue placeholder={L("Your subjects", "Vos matières")} /></SelectTrigger>
                    <SelectContent>{allowed.map((sub) => <SelectItem key={sub} value={sub}>{sub}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={g.subject} onChange={(e) => setG({ ...g, subject: e.target.value })} data-testid="tgrade-subject" />
                )}
              </Field>
              <Field label={L("Category", "Catégorie")}><Select value={g.category} onValueChange={(v) => setG({ ...g, category: v })}><SelectTrigger data-testid="tgrade-category"><SelectValue /></SelectTrigger><SelectContent>{GRADE_CATS.map((c) => <SelectItem key={c.v} value={c.v}>{L(c.en, c.fr)}</SelectItem>)}</SelectContent></Select></Field>
              <Field label={L("Score", "Note")}><Input type="number" value={g.score} onChange={(e) => setG({ ...g, score: e.target.value })} data-testid="tgrade-score" /></Field>
              <Field label={L("Out of", "Sur")}><Input type="number" value={g.max_score} onChange={(e) => setG({ ...g, max_score: e.target.value })} /></Field>
              <Button onClick={add} disabled={locked} data-testid="tgrade-add"><Plus className="h-4 w-4 mr-1" />{L("Add", "Ajouter")}</Button>
            </div>
            <Field label={L("Remarks", "Remarques")}><Input value={g.remarks} onChange={(e) => setG({ ...g, remarks: e.target.value })} /></Field>
          </div>
          <div className="bg-card border border-border rounded-lg p-5 flex flex-wrap items-center gap-3" data-testid="tgrade-submission-panel">
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold">{L("Submit for approval", "Soumettre pour approbation")}</p>
              <p className="text-xs text-muted-foreground">{g.subject ? L(`Subject "${g.subject}" · ${term}`, `Matière « ${g.subject} » · ${term}`) : L("Choose a subject above first.", "Choisissez d'abord une matière ci-dessus.")}</p>
            </div>
            {subStatus && subStatus.status !== "none" && <GradeSubmissionBadge status={subStatus.status} L={L} />}
            <Button onClick={submit} disabled={!g.subject || locked} data-testid="tgrade-submit-btn"><Save className="h-4 w-4 mr-1" />{locked ? L("Locked", "Verrouillé") : L("Submit", "Soumettre")}</Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3 border-t border-border pt-3" data-testid="moyenne-rule-note">
            {L("How the grade is computed: per subject we combine Exam, Quizzes, Projects and Participation using the school's weighted formula (default Exam 50% / Quizzes 20% / Projects 20% / Participation 10%). Extra-credit points are added on top. Subjects are then weighted by their official bulletin coefficient to produce the moyenne /20.",
               "Calcul de la note : par matière on combine Examen, Quiz, Projets et Participation avec la formule pondérée de l'école (par défaut Examen 50 % / Quiz 20 % / Projets 20 % / Participation 10 %). Les points bonus s'ajoutent par-dessus. Chaque matière est ensuite pondérée par son coefficient officiel du bulletin pour obtenir la moyenne /20.")}
          </p>
        </div>
      )}
    </div>
  );
}

function Schedule({ L }) {
  const { data: classes, loading } = useFetch(() => api.get("/schedule/me"));
  const dlPdf = async () => {
    try { const r = await api.raw.get("/schedule/me/pdf", { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = "horaire.pdf"; a.click(); URL.revokeObjectURL(u); }
    catch (e) { toast.error(L("Download failed", "Échec du téléchargement")); }
  };
  const BREAK_RE = /lunch|breakfast|dinner|recess|break|pause|repas|cantine|d[eé]jeuner|d[îi]ner|r[eé]cr|snack|go[uû]ter|collation/i;
  const byDay = useMemo(() => {
    const map = {}; DAYS.forEach((d) => (map[d] = []));
    (classes || []).forEach((c) => (c.schedule || []).forEach((s) => { if (map[s.day]) map[s.day].push({ ...s, className: c.name, color: c.color, isBreak: BREAK_RE.test(s.subject || ""), isTitulaire: !!c.teacher_id && (s.teacher_id || c.teacher_id) === c.teacher_id }); }));
    DAYS.forEach((d) => map[d].sort((a, b) => a.start.localeCompare(b.start)));
    return map;
  }, [classes]);
  const COLOR_CLS = { blue: "border-blue-500 bg-blue-50", emerald: "border-emerald-500 bg-emerald-50", amber: "border-amber-500 bg-amber-50", purple: "border-purple-500 bg-purple-50", rose: "border-rose-500 bg-rose-50", cyan: "border-cyan-500 bg-cyan-50", indigo: "border-indigo-500 bg-indigo-50", orange: "border-orange-500 bg-orange-50" };
  if (loading) return <Empty title={L("Loading…", "Chargement…")} />;
  return (
    <div className="animate-fade-up">
      <PageHeader title={L("Weekly Schedule", "Horaire hebdomadaire")}
        action={<Button onClick={dlPdf} data-testid="schedule-pdf-btn"><Download className="h-4 w-4 mr-1" />{L("Download PDF", "Télécharger le PDF")}</Button>} />
      {(() => {
        const periodMap = {};
        DAYS.forEach((d) => byDay[d].forEach((s) => { periodMap[`${s.start}-${s.end}`] = { start: s.start, end: s.end }; }));
        const periods = Object.values(periodMap).sort((a, b) => a.start.localeCompare(b.start));
        const cell = {};
        DAYS.forEach((d) => { cell[d] = {}; byDay[d].forEach((s) => { cell[d][`${s.start}-${s.end}`] = s; }); });
        const FR = { Monday: "Lundi", Tuesday: "Mardi", Wednesday: "Mercredi", Thursday: "Jeudi", Friday: "Vendredi" };
        return (
          <div className="overflow-x-auto rounded-lg border border-border">
            <div className="relative min-w-[900px] grid" style={{ gridTemplateColumns: "104px repeat(5, 1fr)" }}>
              <img src={`${API}/settings/logo`} alt="" aria-hidden className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] max-w-[55%] opacity-[0.06] z-0" data-testid="schedule-watermark" />
              <div className="z-10 bg-secondary/40 border-b border-border" />
              {DAYS.map((d) => <div key={d} className="z-10 bg-secondary/40 border-b border-l border-border py-2 text-center font-heading font-bold text-primary">{L(d, FR[d])}</div>)}
              {periods.length === 0 ? (
                <div className="z-10 text-center text-muted-foreground py-8" style={{ gridColumn: "1 / -1" }}>{L("No schedule available", "Aucun horaire disponible")}</div>
              ) : periods.map((p, ri) => (
                <Fragment key={ri}>
                  <div className="z-10 border-b border-border px-2 py-2 text-xs font-medium text-muted-foreground flex items-center" data-testid={`sched-period-${p.start}`}>{p.start}–{p.end}</div>
                  {DAYS.map((d) => { const s = cell[d][`${p.start}-${p.end}`]; return (
                    <div key={d} className="z-10 border-b border-l border-border p-1 min-w-0">
                      {s && <div className={`h-full rounded-md border-l-4 pl-2 pr-1 py-1 ${s.isBreak ? "border-gray-400 bg-gray-100 border-dashed" : (COLOR_CLS[s.color] || "border-accent bg-secondary/40")}`}>
                        <p className="text-sm font-semibold flex items-center gap-1 break-words">{s.isBreak && <Utensils className="h-3 w-3 shrink-0" />}{s.subject}</p>
                        <p className="text-xs text-muted-foreground break-words">{s.className}{s.teacher_name ? ` · ${s.teacher_name}` : ""}</p>
                        {!s.isBreak && s.isTitulaire && <span className="mt-0.5 inline-block rounded bg-primary/10 text-primary text-[9px] font-semibold px-1 py-0.5" data-testid="sched-titulaire-badge">{L("Class Teacher", "Titulaire de Classe")}</span>}
                      </div>}
                    </div>
                  ); })}
                </Fragment>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
